// БУГ.3: приоритеты атаки для Глобала (диздок §7.3).
//
// При размещении каждого Глобал-юнита игрок выбирает один из 4 приоритетов
// атаки. Выбор фиксируется до конца игры (хранится в card.attackPriority).
// applyPriority в priorities.js вызывает pickGlobalTarget когда attacker
// имеет faction='global' и attackPriority. Если у юнита нет attackPriority
// (бот, sandbox или legacy) — applyPriority использует random-фолбэк.

// Список приоритетов для UI-picker'а.
//   strongest — наибольшая АТАКА среди доступных (current.atk)
//   weakest   — наименьшее ТЕКУЩЕЕ здоровье (current.hp)
//   healer    — юниты с хил-способностью в приоритете
//   random    — случайный
export const GLOBAL_PRIORITIES = Object.freeze([
  { id: 'strongest', label_ru: 'Самый сильный', label_en: 'Strongest',
    hint_ru: 'Кто бьёт больнее', hint_en: 'Highest attack' },
  { id: 'weakest', label_ru: 'Самый слабый', label_en: 'Weakest',
    hint_ru: 'У кого меньше HP', hint_en: 'Lowest current HP' },
  { id: 'healer', label_ru: 'Кто лечит', label_en: 'Healer',
    hint_ru: 'Юниты с хил-способностью', hint_en: 'Units with heal ability' },
  { id: 'random', label_ru: 'Случайный', label_en: 'Random',
    hint_ru: 'Без предпочтений', hint_en: 'No preference' }
]);

const VALID_PRIORITIES = new Set(GLOBAL_PRIORITIES.map((p) => p.id));

export function isValidGlobalPriority(id) {
  return typeof id === 'string' && VALID_PRIORITIES.has(id);
}

// Юнит считается «хилером» если в ability_text есть упоминание хила.
// Это эвристика для эпохи прототипа — на проде нужен явный keyword/тег
// на карте (например `tags: ['healer']`). Пока живём так.
function isHealer(card) {
  if (!card) return false;
  // Явный тег если когда-нибудь добавим в xlsx.
  if (Array.isArray(card.tags) && card.tags.includes('healer')) return true;
  const at = card.ability_text || {};
  const blob = [
    at.order, at.trigger, at.trigger1, at.trigger2,
    at.deathrattle, at.active, at.aura,
    card.ability_short
  ].filter(Boolean).join(' ').toLowerCase();
  return /\bхил\w*\b|\bлеч\w*\b|\b\+\s*\d+\s*hp\b|\bheal\w*\b/.test(blob);
}

// Главный picker. Сигнатура совместима с применением в priorities.js:
//   pickGlobalTarget(attacker, pool, rng)
// Возвращает выбранную цель из pool, или null если pool пуст.
export function pickGlobalTarget(attacker, pool, rng) {
  if (!pool || pool.length === 0) return null;
  const priority = attacker?.attackPriority;

  switch (priority) {
    case 'strongest':
      return pickByMetric(pool, (c) => readStat(c, 'atk'), /*max=*/ true);
    case 'weakest':
      return pickByMetric(pool, (c) => readStat(c, 'hp'), /*max=*/ false);
    case 'healer': {
      const healers = pool.filter(isHealer);
      if (healers.length > 0) {
        // Tie-break — позиция в массиве (line 0 слева направо, потом line 1).
        return healers[0];
      }
      // Нет хилеров — random чтобы не давать преимущество позиции.
      return pool[safeRandomInt(rng, pool.length)];
    }
    case 'random':
    default:
      return pool[safeRandomInt(rng, pool.length)];
  }
}

// Нужно ли открыть picker при размещении карты. true для Глобал-юнитов
// у которых ещё не выбран приоритет. Используется UI-клиентом.
export function needsAttackPriorityPicker(card) {
  if (!card) return false;
  if (card.faction !== 'global') return false;
  if (card.type !== 'unit') return false;
  return !card.attackPriority;
}

// Совместимость со старым API (используется bot-random.js, main.js).
// Бот не строит долгосрочную стратегию — всегда 'random'. applyPriority
// в priorities.js всё равно даёт random-фолбэк для Global без выбора,
// так что это просто фиксирует card.attackPriority явно.
export function pickRandomGlobalPriority(_rng) {
  return 'random';
}

// Установить выбранный приоритет на карту. Валидирует id; невалидное
// значение → 'random' как безопасный фолбэк.
export function setAttackPriority(card, priority) {
  if (!card) return;
  card.attackPriority = isValidGlobalPriority(priority) ? priority : 'random';
}

// Helper: безопасное чтение current.X с fallback на base.X / card.X.
function readStat(card, key) {
  if (!card) return 0;
  const cur = card.current?.[key];
  if (typeof cur === 'number') return cur;
  const base = card.base?.[key];
  if (typeof base === 'number') return base;
  return card[key] ?? 0;
}

function pickByMetric(arr, metric, max) {
  let best = arr[0];
  let bestVal = metric(best);
  for (let i = 1; i < arr.length; i++) {
    const v = metric(arr[i]);
    if ((max && v > bestVal) || (!max && v < bestVal)) {
      best = arr[i];
      bestVal = v;
    }
  }
  return best;
}

function safeRandomInt(rng, len) {
  if (rng?.int && typeof rng.int === 'function') return rng.int(len);
  return Math.floor(Math.random() * len);
}
